import { Trash2, Recycle, Leaf, Droplets, Home, Building2, GraduationCap, Landmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useT } from "@/i18n";

const Services = () => {
  const t = useT();
  const binServices = [{
    icon: Trash2,
    title: t("Bac noir (ordures)", "Black bin (garbage)"),
    color: "bg-foreground"
  }, {
    icon: Recycle,
    title: t("Bac bleu (recyclage)", "Blue bin (recycling)"),
    color: "bg-primary"
  }, {
    icon: Leaf,
    title: t("Bac brun (compost)", "Brown bin (compost)"),
    color: "bg-secondary"
  }];
  const pressureServices = [{
    icon: Home,
    title: t("Revêtement maison", "House siding"),
    description: t("Nettoyage complet de l'extérieur de votre maison", "Complete cleaning of your home's exterior")
  }, {
    icon: Droplets,
    title: t("Entrée de maison / béton", "Driveway / concrete"),
    description: t("Restaurez l'apparence de vos surfaces bétonnées", "Restore the look of your concrete surfaces")
  }, {
    icon: Building2,
    title: t("Surfaces extérieures", "Outdoor surfaces"),
    description: t("Terrasses, patios, clôtures et plus encore", "Decks, patios, fences and more")
  }];
  const clientTypes = [{
    icon: Home,
    label: t("Résidentiel", "Residential")
  }, {
    icon: Building2,
    label: t("Commercial", "Commercial")
  }, {
    icon: Landmark,
    label: t("Industriel", "Industrial")
  }, {
    icon: GraduationCap,
    label: t("Écoles", "Schools")
  }, {
    icon: Landmark,
    label: t("Municipalités", "Municipalities")
  }];
  return <section id="services" aria-labelledby="services-heading" className="section-padding bg-muted">
      <div className="container-narrow mx-auto">
        {/* Section Header */}
        <header className="text-center mb-16">
          <span className="text-accent font-semibold text-sm uppercase tracking-wider">{t("Nos services", "Our services")}</span>
          <h2 id="services-heading" className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mt-3 mb-4">
            {t("Services de nettoyage de bacs et haute pression", "Bin cleaning and pressure washing services")}
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {t(
              "Du nettoyage de bacs à ordures au lavage haute pression résidentiel à Valleyfield, Beauharnois, Soulanges et Haut-Saint-Laurent.",
              "From garbage bin cleaning to residential pressure washing in Valleyfield, Beauharnois, Soulanges and Haut-Saint-Laurent."
            )}
          </p>
        </header>

        {/* Bin Cleaning Services */}
        <div className="mb-16">
          <div className="bg-card rounded-2xl shadow-card p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-heading text-2xl font-bold text-[#080808]">{t("NETTOYAGE DE BACS    ", "BIN CLEANING    ")}</h3>
                <p className="text-accent font-semibold">{t("Avril à Octobre", "April to October")}</p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {binServices.map((service, index) => <div key={index} className="flex items-center gap-4 p-4 rounded-xl border border-border hover:border-primary/30 hover:shadow-sm transition-all duration-300">
                  <div className={`w-10 h-10 rounded-lg ${service.color} flex items-center justify-center`}>
                    <service.icon className="w-5 h-5 text-background" />
                  </div>
                  <span className="font-semibold text-foreground">{service.title}</span>
                </div>)}
            </div>

            <div className="text-center">
              <Button variant="cta" size="lg" asChild>
                <a href="#soumission">{t("Réserver mon nettoyage", "Book my cleaning")}</a>
              </Button>
            </div>
          </div>
        </div>

        {/* Pressure Washing Services */}
        <div className="mb-16">
          <div className="bg-card rounded-2xl shadow-card p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                <Droplets className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <h3 className="font-heading text-2xl font-bold text-black">{t("NETTOYAGE HAUTE PRESSION ", "PRESSURE WASHING ")}</h3>
                <p className="text-accent font-semibold">{t("Soumission gratuite", "Free quote")}</p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {pressureServices.map((service, index) => <div key={index} className="p-6 rounded-xl border border-border hover:border-secondary/30 hover:shadow-sm transition-all duration-300 hover-lift">
                  <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center mb-4">
                    <service.icon className="w-6 h-6 text-secondary" />
                  </div>
                  <h4 className="font-heading font-bold text-lg text-foreground mb-2">{service.title}</h4>
                  <p className="text-muted-foreground text-sm">{service.description}</p>
                </div>)}
            </div>

            <div className="text-center">
              <Button variant="forest" size="lg" asChild>
                <a href="#soumission">{t("Demander une soumission gratuite", "Request a free quote")}</a>
              </Button>
            </div>
          </div>
        </div>

        {/* Client Types */}
        <div className="text-center">
          <h3 className="font-heading text-xl font-bold text-foreground mb-6">{t("Types de clients desservis", "Types of clients we serve")}</h3>
          <div className="flex flex-wrap justify-center gap-4">
            {clientTypes.map((client, index) => <div key={index} className="flex items-center gap-2 bg-card rounded-full px-5 py-3 shadow-sm border border-border">
                <client.icon className="w-5 h-5 text-primary" />
                <span className="font-medium text-foreground">{client.label}</span>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
};
export default Services;
